// Package imports:
import React from 'react';
import { Form, useFormikContext } from 'formik';
// Component imports:
import Input from '../../../ui-elements/Input/Formik/Input';
import DropSelect from '../../../ui-elements/DropSelect/DropSelect';
import DropSelectSearch from '../../../ui-elements/DropSelect/Formik/DropSelectSearch';
import Button from '../../../ui-elements/Button/Button';
import { FormObserver } from './CompanyFormObserver';
import Checkbox from '../../../ui-elements/Input/Formik/Checkbox';
// Service imports:
import { isCompany } from '../../../services/utils';
// Type imports:
import { AddCompanyFormValues, ServiceCompanyEmployee } from "../../../types/DueDiligenceTypes";
// Static data imports:
import { CountryList } from '../../../assets/staticData/CountryList';
import { IToastFromParent } from '../../../types/Types';

interface IProps {
    values: AddCompanyFormValues;
    employees: ServiceCompanyEmployee[];
    closeModal(): void;
    disableSubmit: boolean;
}

export const AddCompanyForm: React.FC<IProps & IToastFromParent> = ({
    values,
    employees,
    closeModal,
    disableSubmit,
    setToastShow,
    setToastMessage,
    setToastType
}) => {
    const [showLoading, setShowLoading] = React.useState(false);
    const { setFieldValue, getFieldMeta, resetForm } = useFormikContext<AddCompanyFormValues>();
    const countryCodeFieldMeta = getFieldMeta("foreignCompanyCountryCode");

    return (
        <Form>
            <FormObserver
                setToastShow={setToastShow}
                setToastMessage={setToastMessage}
                setToastType={setToastType}
                setShowLoading={setShowLoading}
            />
            <div className="form__body">
                <div className="form__row">
                    <div className="form__col">
                        <h6>Upplýsingar um lögaðila</h6>
                    </div>
                </div>
                <div className="form__row">
                    <div className="form__col">
                        <Checkbox
                            label="Lögaðili er erlendur"
                            name="foreign"
                            id="foreign"
                            value={values.foreign.toString()}
                            onChange={(e) => {
                                resetForm();
                                setFieldValue("foreign", !values.foreign);
                            }}
                        />
                    </div>
                </div>
                {values.foreign === false ? (
                    <div className="non-foreign-form">
                        <div className="form__row">
                            <div className="form__col">
                                <Input
                                    label="Kennitala lögaðila"
                                    inputType='ssnFormat'
                                    format='######-####'
                                    name="companySSN"
                                    id="companySSN"
                                    value={values.companySSN}
                                    showLoading={showLoading}
                                />
                            </div>
                            <div className="form__col">
                                <Input
                                    label="Nafn lögaðila"
                                    name="companyName"
                                    id="companyName"
                                    disabled
                                    value={values.companyName}
                                />
                            </div>
                        </div>
                        <div className="form__row">
                            <div className="form__col">
                                <DropSelect
                                    clearInput={true}
                                    usePortal={true}
                                    label="Umsjónaraðili (valkvætt)"
                                    options={employees.map(emp => ({
                                        id: String(emp.webUserId),
                                        label: emp.fullname,
                                        value: String(emp.webUserId)
                                    }))}
                                    defaultValue={null}
                                    onChange={val => setFieldValue("supervisorId", val)}
                                    size="lg"
                                />
                            </div>
                        </div>
                    </div>
                ) : (
                    <div>
                        <div className="form__row">
                            <div className="form__col">
                                <Input
                                    label="Nafn lögaðila"
                                    name="foreignCompanyName"
                                    id="foreignCompanyName"
                                    value={values.foreignCompanyName}
                                />
                            </div>
                            <div className="form__col">
                                <Input
                                    label="Póstnúmer"
                                    name="foreignCompanyPostalCode"
                                    id="foreignCompanyPostalCode"
                                    value={values.foreignCompanyPostalCode}
                                />
                            </div>
                        </div>
                        <div className="form__row">
                            <div className="form__col">
                                <Input
                                    label="Heimilisfang"
                                    name="foreignCompanyAddress"
                                    id="foreignCompanyAddress"
                                    value={values.foreignCompanyAddress}
                                />
                            </div>
                            <div className="form__col">
                                <Input
                                    label="Svæði"
                                    name="foreignCompanyRegion"
                                    id="foreignCompanyRegion"
                                    value={values.foreignCompanyRegion}
                                />
                            </div>
                        </div>
                        <div className="form__row">
                            <div className="form__col">
                                <DropSelectSearch
                                    clearInput={true}
                                    usePortal={true}
                                    error={
                                        countryCodeFieldMeta.touched && countryCodeFieldMeta.error ? countryCodeFieldMeta.error : undefined
                                    }
                                    selectSize="lg"
                                    label="Land"
                                    name="foreignCompanyCountry"
                                    id="foreignCompanyCountry"
                                    options={CountryList}
                                    defaultValue={values.foreignCompanyCountry}
                                    onSelectChange={(cc?: string) => setFieldValue("foreignCompanyCountry", cc)}
                                />
                            </div>
                            <div className="form__col">
                                <DropSelect
                                    clearInput={true}
                                    usePortal={true}
                                    label="Umsjónaraðili (valkvætt)"
                                    options={employees.map(emp => ({
                                        id: String(emp.webUserId),
                                        label: emp.fullname,
                                        value: String(emp.webUserId)
                                    }))}
                                    defaultValue={null}
                                    onChange={val => setFieldValue("supervisorId", val)}
                                    size="lg"
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <div className="form__actions">
                <Button
                    type="button"
                    buttonType="secondary"
                    size="lg"
                    onClick={closeModal}
                >
                    Hætta við
                </Button>
                <Button
                    type="submit"
                    buttonType="primary"
                    size="lg"
                    disabled={disableSubmit}
                >
                    Staðfesta
                </Button>
            </div>
        </Form>
    )
}

export const validate = (values: AddCompanyFormValues) => {
    const errors: { [key: string]: string } = {};
    if (!values.foreign) {
        // Only validate SSN if not foreign
        if (!values.companySSN) {
            errors.companySSN = 'Kennitala er nauðsynleg';
        } else if (!isCompany(values.companySSN)) {
            errors.companySSN = 'Ógild kennitala lögaðila';
        }
    } else {
        // Only validate foreign fields if foreign is checked
        if (!values.foreignCompanyName) errors.foreignCompanyName = 'Nafn er nauðsynlegt';
        if (!values.foreignCompanyPostalCode) errors.foreignCompanyPostalCode = 'Póstnúmer er nauðsynlegt';
        if (!values.foreignCompanyAddress) errors.foreignCompanyAddress = 'Heimilisfang er nauðsynlegt';
        if (!values.foreignCompanyRegion) errors.foreignCompanyRegion = 'Svæði er nauðsynlegt';
        if (!values.foreignCompanyCountry) errors.foreignCompanyCountry = 'Ríkisfang er nauðsynlegt';
    }
    return errors;
}